import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { 
  Search, Users, CheckCircle, ArrowRight, 
  Star, MapPin, Wrench, Briefcase, Code, Palette, Home, Truck, 
  GraduationCap, Heart, Music, Camera, Scissors, 
  Utensils, Zap, Globe, Shield, Smartphone, Coffee,
  Car, Hammer, Paintbrush, Leaf, Baby, Dog, 
  Dumbbell, Plane, ShoppingBag, Stethoscope, Scale,
  BookOpen, Lightbulb, Wifi
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icon mapping for categories
const ICON_MAP = {
  Wrench, Briefcase, Code, Palette, Home, Truck, 
  GraduationCap, Heart, Music, Camera, Scissors, 
  Utensils, Zap, Globe, Shield, Smartphone, Coffee,
  Car, Hammer, Paintbrush, Leaf, Baby, Dog, 
  Dumbbell, Plane, ShoppingBag, Stethoscope, Scale,
  BookOpen, Lightbulb, Wifi, Search, Users
};

const LandingPage = ({ user, onLogout }) => {
  const { t } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [featuredServices, setFeaturedServices] = useState([]);

  useEffect(() => {
    fetchCategories();
    fetchFeaturedServices();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchFeaturedServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setFeaturedServices(response.data.slice(0, 6));
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const getCurrencySymbol = (currency) => {
    const symbols = { PHP: '₱', USD: '$', EUR: '€', GBP: '£' };
    return symbols[currency] || currency;
  };

  const getCategoryIcon = (category) => {
    // If it's a Lucide icon name
    if (category.icon_type === 'lucide' && ICON_MAP[category.icon]) {
      const IconComponent = ICON_MAP[category.icon];
      return <IconComponent className="w-8 h-8" strokeWidth={1.5} />;
    }
    // If it's an emoji or text
    if (category.icon && category.icon.length <= 4) {
      return <span className="text-3xl">{category.icon}</span>;
    }
    // Default icon
    return <Briefcase className="w-8 h-8" strokeWidth={1.5} />;
  };

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <Navbar user={user} onLogout={onLogout} />

      {/* Hero Section */}
      <div className="bg-gray-900 text-white" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <img 
                src="/logo-white.png" 
                alt="GoodJobPH" 
                className="h-14 w-14 object-contain"
              />
              <span className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-none">
                <span className="text-white">Good</span><span className="text-orange-500">Job</span>
                <span className="text-white ml-3">Philippines</span>
              </span>
            </div>
            <h1 className="text-4xl text-gray-200 sm:text-5xl lg:text-5xl font-bold tracking-tight mb-6" data-testid="hero-title">
              {t('home.heroTitle')}
            </h1>
            <p className="text-xl text-gray-400 mb-10 leading-relaxed max-w-2xl" data-testid="hero-description">
              {t('home.heroSubtitle')}
            </p>
            <div className="flex flex-wrap gap-4">
              {user ? (
                <Link
                  to="/services"
                  className="inline-flex items-center gap-2 bg-white text-gray-900 px-8 py-4 rounded-full font-semibold hover:bg-gray-100 transition"
                  data-testid="hero-browse-button"
                >
                  <Search className="w-5 h-5" />
                  {t('nav.browseServices')}
                </Link>
              ) : (
                <>
                  <Link
                    to="/register"
                    className="inline-flex items-center gap-2 bg-white text-gray-900 px-8 py-4 rounded-full font-semibold hover:bg-gray-100 transition"
                    data-testid="hero-get-started-button"
                  >
                    {t('nav.register')}
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                  <Link
                    to="/services"
                    className="inline-flex items-center gap-2 border-2 border-white/30 text-white px-8 py-4 rounded-full font-semibold hover:bg-white/10 transition"
                    data-testid="hero-explore-button"
                  >
                    <Search className="w-5 h-5" />
                    {t('home.exploreServices')}
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Categories Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="categories-section">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4" data-testid="categories-title">{t('landing.popularCategories')}</h2>
          <p className="text-gray-500 max-w-2xl mx-auto">{t('landing.popularCategoriesSub')}</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {categories.slice(0, 10).map((category) => (
            <Link
              key={category.id}
              to={`/services?category=${category.name}`}
              className="group bg-gray-50 hover:bg-gray-100 p-6 rounded-2xl transition-all duration-300 text-center border border-gray-100 hover:border-gray-200 hover:shadow-sm"
              data-testid={`category-card-${category.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="text-gray-400 group-hover:text-gray-700 transition mb-3 flex justify-center">
                {getCategoryIcon(category)}
              </div>
              <h3 className="font-semibold text-gray-900 text-sm">{category.name}</h3>
            </Link>
          ))}
        </div>
      </div>

      {/* Featured Services */}
      {featuredServices.length > 0 && (
        <div className="bg-gray-50 py-20" data-testid="featured-services-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4" data-testid="featured-title">{t('landing.featuredServices')}</h2>
              <p className="text-gray-500 max-w-2xl mx-auto">{t('landing.featuredServicesSub')}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredServices.map((service) => (
                <Link
                  key={service.id}
                  to={`/services/${service.id}`}
                  className="bg-white rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-100 hover:border-gray-200 hover:-translate-y-1"
                  data-testid={`service-card-${service.id}`}
                >
                  {/* Service Image */}
                  <div className="relative h-48 bg-gray-100">
                    {service.images && service.images.length > 0 ? (
                      <img 
                        src={service.images[0].startsWith('/') ? `${BACKEND_URL}${service.images[0]}` : service.images[0]} 
                        alt={service.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                        <Briefcase className="w-12 h-12 text-gray-400" />
                      </div>
                    )}
                    {/* Category badge overlaid on image */}
                    <span className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm text-gray-700 text-sm px-3 py-1 rounded-full font-medium shadow-sm">
                      {service.category}
                    </span>
                    {/* Rating badge overlaid on image */}
                    {service.rating > 0 && (
                      <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm flex items-center gap-1 px-2 py-1 rounded-full shadow-sm">
                        <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                        <span className="text-sm font-semibold text-gray-700">{service.rating}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="p-5">
                    <h3 className="text-lg font-bold mb-2 text-gray-900">{service.title}</h3>
                    <p className="text-gray-500 mb-4 line-clamp-2 text-sm">{service.description}</p>
                    
                    {/* Provider info with avatar */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {/* Provider avatar */}
                        <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center overflow-hidden flex-shrink-0">
                          {service.provider_avatar ? (
                            <img 
                              src={service.provider_avatar.startsWith('/') ? `${BACKEND_URL}${service.provider_avatar}` : service.provider_avatar} 
                              alt={service.provider_name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-white font-bold text-sm">
                              {service.provider_name?.charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-700">{service.provider_name}</p>
                          {service.location && (
                            <p className="text-xs text-gray-400 flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              <span className="truncate max-w-[150px]">{service.location}</span>
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-gray-900">
                          {getCurrencySymbol(service.currency)}{service.price?.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link
                to="/services"
                className="inline-flex items-center gap-2 bg-gray-900 text-white px-8 py-4 rounded-full font-semibold hover:bg-gray-800 transition"
                data-testid="view-all-services-button"
              >
                {t('landing.viewAllServices')}
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* How It Works */}
      <div className="py-20" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('landing.howItWorks')}</h2>
            <p className="text-gray-500 max-w-2xl mx-auto">{t('landing.howItWorksSub')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center" data-testid="step-1">
              <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-5">
                <Search className="w-7 h-7 text-gray-700" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('landing.step1Title')}</h3>
              <p className="text-gray-500">
                {t('landing.step1Desc')}
              </p>
            </div>
            <div className="text-center" data-testid="step-2">
              <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-5">
                <Users className="w-7 h-7 text-gray-700" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('landing.step2Title')}</h3>
              <p className="text-gray-500">
                {t('landing.step2Desc')}
              </p>
            </div>
            <div className="text-center" data-testid="step-3">
              <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-5">
                <CheckCircle className="w-7 h-7 text-gray-700" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('landing.step3Title')}</h3>
              <p className="text-gray-500">
                {t('landing.step3Desc')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gray-900 py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">{t('landing.ctaTitle')}</h2>
          <p className="text-gray-400 mb-8 text-lg">
            {t('landing.ctaSub')}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/register"
              className="inline-flex items-center gap-2 bg-white text-gray-900 px-8 py-4 rounded-full font-semibold hover:bg-gray-100 transition"
            >
              {t('landing.createAccount')}
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/services"
              className="inline-flex items-center gap-2 border-2 border-white/30 text-white px-8 py-4 rounded-full font-semibold hover:bg-white/10 transition"
            >
              {t('nav.browseServices')}
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default LandingPage;
